import {AvailabilityInfo, ConfigApi, OrderType} from "../../../Api/types";
import {TFunction} from "i18next";
import {DateHelper, getHourTimeString} from "../../../Common/Date/DateHelper";
import ItemHelper from "./ItemHelper";
import AvailableHelper from "../Availability/AvailableHelper";
import HoursHelper from "../Availability/HoursHelper";
import CategoryHelper from "../Category/CategoryHelper";
import DateService from "../../../Common/Date/DateService";
import {getParsedSessionOrderType} from "../../../../utils/order";

class ItemAvailabilityHelper {

    static isItemAvailable = (
        item: Record<string, any>,
        availabilities: Record<string, any>[],
        items: Record<string, any>[],
        session: Record<string, any>,
        locale: string | undefined,
        config: ConfigApi,
        menus: Record<string, any>[],
        categories: Record<string, any>[],
        promotions: Record<string, any>[],
        cart: Record<string, any>,
        itemGroups: Record<string, any>[],
        categoryFromUrlState: Record<string, any> | undefined,
        parsedCurrentType: string,
        categoryMenus: Record<string, any>[]
    ) => {
        const availability = AvailableHelper.getAvailability(availabilities, item.availability_id);
        const isOpen = availability ? HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(session.pickup_at)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)) : true;
        const menusWithCategoryForCurrentOrderType = categoryMenus.filter((menu: any) => menu[parsedCurrentType.toLowerCase()] === true);
        const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: any) => menu.availability_id));
        const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(session.pickup_at));
        const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));
        const isCategoryAvailable = isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;

        return isCategoryAvailable && isOpen && !item.disabled;
    }

    static getItemOrderTypesAvailabilities = (
        item: Record<string, any>,
        availability: Record<string, any> | undefined,
        category: Record<string, any>,
        menus: Record<string, any>[],
        availabilities: Record<string, any>[],
        pickupDate: string | undefined,
        config: ConfigApi | undefined,
    ) => {
        const itemAvailabilities = availability ? [availability] : [];
        const storeOpenHours = config?.hours || [];

        return (Object.keys(OrderType) as OrderType[]).reduce((acc: any, type) => {
            const menusWithCategory = CategoryHelper.getMenusForCategory(menus, category);
            const menusWithCategoryForOrderType = menusWithCategory.filter((menu: any) => menu[getParsedSessionOrderType(type).toLowerCase()] === true);
            const menuAvailabilitiesForType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForOrderType.map((menu: any) => menu.availability_id));
            let nearestAvailableDate = undefined;
            let searchDate = DateService.emptyOrNow(pickupDate);

            const maxSearchTries = 30;
            let currentSearchTry = 0;
            while (!nearestAvailableDate && !item.item_always_available) {
                if (currentSearchTry >= maxSearchTries) break;

                const maxSearchDate = new Date(searchDate);
                maxSearchDate.setDate(maxSearchDate.getDate() + 30);
                const nextItemAvailableDate = ItemHelper.getNearestAvailabilityDateForItem(item, itemAvailabilities, searchDate ? DateHelper.format(searchDate) : undefined, type, maxSearchDate);

                if (nextItemAvailableDate.dateFrom) {
                    AvailableHelper.trimHoursToAvailabilities(menuAvailabilitiesForType, nextItemAvailableDate);
                    HoursHelper.trimHoursToStore(storeOpenHours, nextItemAvailableDate);
                }

                if (nextItemAvailableDate.dateTo?.getTime() === nextItemAvailableDate.dateFrom?.getTime()) {
                    searchDate.setDate(searchDate.getDate() + 1);
                    searchDate.setHours(0, 0, 0, 0);
                    currentSearchTry++;
                    continue;
                }

                if (nextItemAvailableDate.dateFrom
                    && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, itemAvailabilities, nextItemAvailableDate.dateFrom)
                    && searchDate.getTime() <= nextItemAvailableDate.dateFrom.getTime()
                ) {
                    nearestAvailableDate = nextItemAvailableDate;
                    break;
                } else if (nextItemAvailableDate.dateTo
                    && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, itemAvailabilities, nextItemAvailableDate.dateTo)
                    && searchDate.getTime() < nextItemAvailableDate.dateTo.getTime()
                ) {
                    nearestAvailableDate = nextItemAvailableDate;
                    break;
                }

                searchDate.setDate(searchDate.getDate() + 1);
                searchDate.setHours(0, 0, 0, 0);
                currentSearchTry++;
            }

            if (!nearestAvailableDate) {
                currentSearchTry = 0;
                searchDate = DateService.emptyOrNow(pickupDate);
                const menusWithCategory = CategoryHelper.getMenusForCategory(menus, category);
                const menusWithCategoryForOrderType = menusWithCategory.filter((menu: any) => menu[getParsedSessionOrderType(type).toLowerCase()] === true);
                const menuAvailabilitiesForType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForOrderType.map((menu: any) => menu.availability_id));

                while (!nearestAvailableDate) {
                    if (currentSearchTry >= maxSearchTries) break;

                    for (const _ of menuAvailabilitiesForType) {
                        const availabilityDate = new Date(searchDate);
                        availabilityDate.setHours(0, 0, 0, 0);
                        const date: { dateFrom: Date, dateTo?: Date } = {
                            dateFrom: new Date(availabilityDate),
                            dateTo: undefined,
                        };
                        AvailableHelper.trimHoursToAvailabilities(menuAvailabilitiesForType, date);
                        HoursHelper.trimHoursToStore(storeOpenHours, date);

                        if (date.dateTo?.getTime() === date.dateFrom?.getTime()) {
                            searchDate.setDate(searchDate.getDate() + 1);
                            searchDate.setHours(0, 0, 0, 0);
                            currentSearchTry++;
                            continue;
                        }

                        if (date.dateFrom && date.dateTo && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, itemAvailabilities, date.dateFrom)
                            && searchDate.getTime() <= date.dateFrom?.getTime()
                        ) {
                            nearestAvailableDate = date;
                            break;
                        } else if (date.dateTo) {
                            const compareDateTo = new Date(date.dateTo);
                            if (compareDateTo <= date.dateFrom) {
                                compareDateTo.setDate(compareDateTo.getDate() + 1);
                            }
                            if (date.dateTo && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, itemAvailabilities, date.dateFrom)
                                && searchDate.getTime() < (date.dateTo as any)?.getTime()) {
                                nearestAvailableDate = date;
                                break;
                            }
                        }
                    }

                    searchDate.setDate(searchDate.getDate() + 1);
                    searchDate.setHours(0, 0, 0, 0);
                    currentSearchTry++;
                }
            }

            acc[type] = nearestAvailableDate;

            return acc;
        }, {});
    }

    static getItemAvailabilityInfo = (
        item: Record<string, any>,
        availability: Record<string, any> | undefined,
        config: ConfigApi | undefined,
        menus: Record<string, any>[],
        availabilities: Record<string, any>[],
        category: Record<string, any>,
        pickupDate: string | undefined,
        orderType: OrderType,
        locale: string | undefined,
        t: TFunction,
    ): AvailabilityInfo[] => {
        const orderTypesAvailabilities = ItemAvailabilityHelper.getItemOrderTypesAvailabilities(item, availability, category, menus, availabilities, pickupDate, config);

        const infos: AvailabilityInfo[] = [];

        const typesDateGroups: {
            types: string[],
            dateFrom: Date,
            dateTo: Date,
        }[] = [];
        const typesAvailabilities = Object.keys(orderTypesAvailabilities)
            .map(type => {
                if (!orderTypesAvailabilities[type]) return undefined;

                return orderTypesAvailabilities[type];
            })
            .filter(Boolean)
            .sort((a, b) => a.dateFrom.getTime() - b.dateFrom.getTime());

        if (typesAvailabilities.length) {
            Object.keys(orderTypesAvailabilities)
                .forEach(type => {
                    const otherTypesAvailability = orderTypesAvailabilities[type];
                    if (otherTypesAvailability) {
                        const index = typesDateGroups.findIndex(group => group?.dateFrom.getTime() === otherTypesAvailability?.dateFrom.getTime()
                            && group.dateTo?.getTime() === otherTypesAvailability?.dateTo.getTime())
                        if (index > -1) {
                            typesDateGroups[index].types.push(type);
                        } else {
                            typesDateGroups.push({
                                types: [type],
                                dateFrom: otherTypesAvailability.dateFrom,
                                dateTo: otherTypesAvailability.dateTo,
                            })
                        }
                    }
                });

            typesDateGroups.forEach(group => {
                infos.push({
                    text: `${t("modules.menu.field.available.title")}:`,
                    typesText: group.types.map(type => t(`enums.order.type.${type}`)).join(", "),
                    dateText: `${DateHelper.getFormattedNearestDate(DateHelper.format(group.dateFrom), t, locale ?? "", true)} - ${getHourTimeString(group.dateTo)}`,
                    currentType: group.types.length === 1 && group.types[0] === orderType,
                    isNowAvailable: group.dateFrom && group.dateTo && new Date() >= group.dateFrom && new Date() < group.dateTo,
                    includeCurrentType: group.types.includes(orderType),
                });

                const firstIndex = infos.findIndex(i => i.isNowAvailable);
                const nowAvailableInfos = infos.filter((i, index) => i.isNowAvailable && index > firstIndex);
                if (firstIndex > -1) {
                    nowAvailableInfos.forEach(i => {
                        const index = infos.findIndex(info => info.typesText === i.typesText);
                        if (index > -1) {
                            infos[firstIndex].typesText = `${infos[firstIndex].typesText}, ${infos[index].typesText}`;
                            infos.splice(index, 1);
                        }
                    })
                }
            });
        }

        return infos.length > 0 ? infos : [{text: t("common.word.not_available")}];
    }

    static getCategoryWithNearestAvailableDateForItem = (
        item: Record<string, any>,
        categories: Record<string, any>[],
        menus: Record<string, any>[],
        session: Record<string, any>,
        itemGroup?: Record<string, any>
    ) => {
        const ids = itemGroup ? itemGroup.items.map((itemGroupItem: Record<string, any>) => itemGroupItem.item_id) : [item.id];
        const itemCategories = categories.filter(category => {
            return category.entities.some((entity: Record<string, any>) => ids.includes(entity.entity_id));
        });
        let minDate: Date | undefined = undefined;

        const itemParsedCategories: {
            category: Record<string, any>;
            nearestDate: {
                dateFrom: Date
                dateTo: Date
            } | undefined;
            isAvailable: boolean;
        }[] = [...itemCategories].map((category: Record<string, any>) => {
            const maxSearchDate = DateService.emptyOrNow(session.pickup_at);
            maxSearchDate.setDate(maxSearchDate.getDate() + 30);
            const menusWithCategory = CategoryHelper.getMenusForCategory(menus, category);
            const orderType = session.order_type as OrderType;
            const menusWithCategoryForOrderType = menusWithCategory.filter((menu: any) => menu[getParsedSessionOrderType(orderType).toLowerCase()] === true);
            const menuAvailabilitiesForType = menusWithCategoryForOrderType.map((menu: any) => menu.availability);
            const nearestDate = AvailableHelper.getNearestOpenDateFromNowOrDate(menuAvailabilitiesForType, DateService.emptyOrNow(session.pickup_at), true, maxSearchDate);
            const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForType.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(session.pickup_at));
            const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForType.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));

            return {
                category,
                nearestDate,
                isAvailable: isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForOrderType.length > 0
            }
        }).sort((a, b) => {
            if (a.isAvailable !== b.isAvailable) {
                return a.isAvailable ? -1 : 1;
            }

            const dateA = a.nearestDate?.dateFrom ? new Date(a.nearestDate.dateFrom).getTime() : Infinity;
            const dateB = b.nearestDate?.dateFrom ? new Date(b.nearestDate.dateFrom).getTime() : Infinity;

            return dateA - dateB;
        });

        let minCategory: any | undefined = itemParsedCategories[0]?.category;

        if (itemParsedCategories.length > 1) {
            itemParsedCategories.forEach(categoryObject => {
                const {category} = categoryObject;
                const menusWithCategory = menus.filter((menu: any) => menu.categories.some((c: any) => c.category_id === category.id));
                const menusAvailabilities = menusWithCategory.map((menu: any) => menu.availability);
                const itemDate = ItemHelper.getNearestAvailabilityDateForItem(item, menusAvailabilities, session.pickup_at, session.order_type as OrderType)?.dateFrom;
                if (itemDate && (!minDate || itemDate < minDate)) {
                    minDate = itemDate;
                    minCategory = category;
                }
            });
        }
        return minCategory;
    }

    static getParsedAvailabilityInfos = (availabilityInfos: AvailabilityInfo[]) => {
        const result: string[] = [];

        if (availabilityInfos.length === 1) {
            const info = availabilityInfos[0];

            if ((info.currentType || info.includeCurrentType) && !info.isNowAvailable && info.dateText) {
                result.push(`${info.text} ${info.dateText.toLowerCase()}`);
            } else if (!info.currentType && !info.includeCurrentType) {
                if (info.isNowAvailable) {
                    result.push(`${info.text} ${info.typesText}`);
                } else {
                    if (info.dateText) {
                        result.push(`${info.text} ${info.typesText}. ${info.dateText}`);
                    } else {
                        result.push(info.text);
                    }
                }
            }

            return result;
        }

        if (availabilityInfos.length > 1) {
            availabilityInfos.forEach((info) => {
                let title = `${info.text} ${info.typesText}`;

                if (!info.isNowAvailable && info.dateText) {
                    title += ` - ${info.dateText.toLowerCase()}`;
                }

                result.push(title);
            })
        }

        return result;
    }
}

export default ItemAvailabilityHelper;